import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, Form, Row, Col } from 'react-bootstrap';
import { useAuthHeader } from 'react-auth-kit';
import * as Icon from 'react-feather';
import { jsPDF } from "jspdf";
import 'jspdf-autotable';
import { MEXICO_CITY_TIME_ZONE } from '../../utils/dateTime';

function Paysheet(props)  {
    const [pastPaysheets, setPastPaysheets] = useState([]);
    const [currentPaysheet, setCurrentPaysheet] = useState([]);
    const [totalBonus, setTotalBonus] = useState({});
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [periods, setPeriods] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const apiUrl = process.env.REACT_APP_URL;
    const authHeader = useAuthHeader();

    const fetchBiweeklyPeriods = () => {
        axios({
            method:'GET',
            url:apiUrl+'/finance/get_biweekly_periods',
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            if (response.data.msg && response.data.msg.Periods) {
                setPeriods(response.data.msg.Periods);
                // Set the current period as default
                const currentPeriod = response.data.msg.Periods.find(p => p.is_current);
                if (currentPeriod) {
                    setSelectedPeriod(currentPeriod);
                }
            }
        })
        .catch(error => {
            console.log("Error fetching biweekly periods:", error);
        });
    }

    const get_paysheet = (userId = null, period = null) => {
        const userIdToUse = userId !== null ? userId : selectedUserId;
        const periodToUse = period || selectedPeriod;

        if (!userIdToUse) return;

        let url = apiUrl+`/finance/get_paysheet?user_id=${userIdToUse}`;

        // Add period parameters if a period is selected
        if (periodToUse) {
            url += `&period_start=${periodToUse.start_date}&period_end=${periodToUse.end_date}`;
        }

        axios({
            method:'GET',
            url: url,
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setPastPaysheets(response.data.msg.Paysheets);
            setCurrentPaysheet(response.data.msg.Current);
            // Reset bonus state when loading new data
            setTotalBonus({});
        })
        .catch(error => {
            alert(error);
            console.log(error);
        });
    }

    const fetchNonAdminUsers = () => {
        axios({
            method:'GET',
            url:apiUrl+'/users/get_all_users?user_type=employees',
            headers: {
                Authorization: authHeader()
            }
        }).then(response => {
            if (response.data.msg && response.data.msg.Users) {
                setUsers(response.data.msg.Users);
                // Set the first user as default if no user is selected
                if (response.data.msg.Users.length > 0 && !selectedUserId) {
                    setSelectedUserId(response.data.msg.Users[0].id);
                }
            }
        })
        .catch(error => {
            console.log("Error fetching non-admin users:", error);
        });
    }

    const handleUserChange = (event) => {
        const userId = event.target.value;
        setSelectedUserId(userId);
        if (userId) {
            get_paysheet(userId, selectedPeriod);
        }
    }

    const handlePeriodChange = (event) => {
        const periodNumber = parseInt(event.target.value);
        const period = periods.find(p => p.period_number === periodNumber);
        setSelectedPeriod(period);
        if (selectedUserId && period) {
            get_paysheet(selectedUserId, period);
        }
    }

    const handleBonusClick = (value, paysheetId) => {
        setTotalBonus(prev => ({
            ...prev,
            [paysheetId]: value
        }));
    }

    const getBonusForPaysheet = (paysheetId) => {
        return parseFloat(totalBonus[paysheetId]) || 0;
    }

    const handlePayClick = () => {
    }

    const handlePaysheet = (paysheet) => {
        console.log('Generating PDF for paysheet:', paysheet);

        const bonusAmount = getBonusForPaysheet(paysheet.id);

        // Create new PDF document
        const doc = new jsPDF();

        // Add title
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('Recibo de Pago', 105, 20, { align: 'center' });

        // Add date range
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(`Período: ${paysheet.start_date} - ${paysheet.end_date}`, 105, 30, { align: 'center' });

        // Add horizontal line
        doc.setLineWidth(0.5);
        doc.line(20, 35, 190, 35);

        // Employee Information
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Información del Empleado', 20, 45);

        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.text(`Nombre: ${paysheet.name}`, 20, 55);

        // Salary breakdown table
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Desglose de Pago', 20, 70);

        // Calculate totals
        const commissionAmount = parseFloat(paysheet.commission) || 0;
        const salaryAmount = parseFloat(paysheet.salary) || 0;
        const totalAmount = salaryAmount + commissionAmount + bonusAmount;

        // Create table data
        const tableData = [
            ['Concepto', 'Monto'],
            ['Salario Base (Semanal)', `$${salaryAmount.toFixed(2)}`],
            ['Comisión', `$${commissionAmount.toFixed(2)}`],
            ['Bono Bisemanal', `$${bonusAmount.toFixed(2)}`],
            ['', ''],
            ['TOTAL A PAGAR', `$${totalAmount.toFixed(2)}`]
        ];

        // Use autoTable for better table formatting
        doc.autoTable({
            startY: 75,
            head: [tableData[0]],
            body: tableData.slice(1),
            theme: 'striped',
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontStyle: 'bold',
                fontSize: 11
            },
            bodyStyles: {
                fontSize: 10
            },
            columnStyles: {
                0: { cellWidth: 120 },
                1: { cellWidth: 60, halign: 'right' }
            },
            didParseCell: function(data) {
                // Make the total row bold
                if (data.row.index === 4 && data.section === 'body') {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fontSize = 12;
                    data.cell.styles.fillColor = [236, 240, 241];
                }
            }
        });

        let finalY = doc.lastAutoTable.finalY + 10;

        // Add trip details if available
        if (paysheet.trips && paysheet.trips.length > 0) {
            // Add page break if needed
            if (finalY > 250) {
                doc.addPage();
                finalY = 20;
            }

            // Trip details header
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('Detalle de Viajes', 20, finalY);

            finalY += 5;

            // Create trips table
            const tripsTableData = paysheet.trips.map(trip => [
                trip.invoice_id || 'N/A',
                trip.client_name || 'N/A',
                trip.start_date || 'N/A',
                trip.end_date || 'N/A',
                // `$${parseFloat(trip.price || 0).toFixed(2)}`,
                trip.role || 'N/A'
            ]);

            doc.autoTable({
                startY: finalY,
                // head: [['Factura', 'Cliente', 'Fecha Inicio', 'Fecha Fin', 'Precio', 'Rol']],
                head: [['Factura', 'Cliente', 'Fecha Inicio', 'Fecha Fin', 'Rol']],
                body: tripsTableData,
                theme: 'grid',
                headStyles: {
                    fillColor: [52, 152, 219],
                    textColor: 255,
                    fontStyle: 'bold',
                    fontSize: 9
                },
                bodyStyles: {
                    fontSize: 8
                },
                columnStyles: {
                    0: { cellWidth: 30 },
                    1: { cellWidth: 40 },
                    2: { cellWidth: 25 },
                    3: { cellWidth: 25 },
                    4: { cellWidth: 25, halign: 'right' },
                    5: { cellWidth: 25 }
                },
                margin: { left: 30, right: 30 }
            });

            finalY = doc.lastAutoTable.finalY + 5;

            // Add trip summary
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`Total de viajes: ${paysheet.trips.length}`, 20, finalY);
            // doc.text(`Valor total de viajes: $${parseFloat(paysheet.total_trip_price || 0).toFixed(2)}`, 20, finalY + 5);

            finalY += 15;
        }

        // Add footer
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');
        doc.text('Este documento es un recibo de pago generado electrónicamente.', 105, finalY, { align: 'center' });
        doc.text(`Generado el: ${new Date().toLocaleDateString('es-MX', { timeZone: MEXICO_CITY_TIME_ZONE })}`, 105, finalY + 5, { align: 'center' });

        // Save the PDF
        const fileName = `Nomina_${paysheet.name.replace(/\s+/g, '_')}_${paysheet.start_date}_${paysheet.end_date}.pdf`;
        doc.save(fileName);
    }

    useEffect(() => {
        fetchNonAdminUsers();
        fetchBiweeklyPeriods();
    }, []);

    useEffect(() => {
        if (selectedUserId && selectedPeriod) {
            get_paysheet(selectedUserId, selectedPeriod);
        }
    }, [selectedUserId, selectedPeriod]);

    return (
        <div className="card mb-4">
            <div className="card-body">
                <div className="card-header d-flex justify-content-between">
                    <h5 className="card-title">Nómina</h5>
                </div>
                <Row className="mb-3">
                    <Col md={4}>
                        <Form.Group className="d-flex align-items-center">
                            <Form.Label className="me-2 mb-0" style={{whiteSpace: 'nowrap'}}>Usuario:</Form.Label>
                            <Form.Select
                                value={selectedUserId}
                                onChange={handleUserChange}
                                style={{width: '200px'}}
                            >
                                <option value="">Seleccionar usuario...</option>
                                <option value="all">📋 Todos los usuarios</option>
                                {users.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.first_name} {user.last_name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="d-flex align-items-center">
                            <Form.Label className="me-2 mb-0" style={{whiteSpace: 'nowrap'}}>Período:</Form.Label>
                            <Form.Select
                                value={selectedPeriod?.period_number ?? ''}
                                onChange={handlePeriodChange}
                                style={{width: '250px'}}
                            >
                                <option value="">Seleccionar período...</option>
                                {periods.map(period => (
                                    <option key={period.period_number} value={period.period_number}>
                                        {period.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                <div>
                    <Table className="m-0" responsive>
                        <thead>
                            <tr>
                                <th></th>
                                <th>SEMANAL</th>
                                <th>BISEMANAL</th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                            </tr>
                            <tr>
                                <th>Nombre</th>
                                <th>Salario Base</th>
                                <th>Comisión</th>
                                <th>Fecha inicio de corte</th>
                                <th>Fecha final de corte</th>
                                <th>Bono Bisemanal</th>
                                <th>Bono+Comisión</th>
                                <th>Generar Descripción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentPaysheet.map(paysheet => (
                                <tr key={paysheet.id}>
                                    <td>{paysheet.name}</td>
                                    <td>{paysheet.salary}</td>
                                    <td>{paysheet.commission?.toFixed(2)}</td>
                                    <td>{paysheet.start_date}</td>
                                    <td>{paysheet.end_date}</td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            max={paysheet.bonus*2}
                                            step={paysheet.bonus}
                                            defaultValue="0"
                                            onChange={(e) => handleBonusClick(e.target.value, paysheet.id)}
                                        />
                                    </td>
                                    <td>{(getBonusForPaysheet(paysheet.id) + (parseFloat(paysheet.commission) || 0)).toFixed(2)}</td>
                                    {/* <td>
                                        <Button type="submit" onClick={() => { handlePayClick(paysheet.earned_bonus);}}>
                                            Pagar
                                        </Button>
                                    </td> */}
                                    <td>
                                        <Button type="submit" onClick={() => { handlePaysheet(paysheet);}}>
                                            Generar
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {pastPaysheets.map(paysheet => (
                                <tr key={paysheet.id}>
                                    <td>{paysheet.name}</td>
                                    <td>{paysheet.salary}</td>
                                    <td>{paysheet.commission}</td>
                                    <td>{paysheet.start_date}</td>
                                    <td>{paysheet.end_date}</td>
                                    <td>{paysheet.bonus}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>
        </div>
    );
}

export default Paysheet;
